<template>
  <HeadlessCombobox
    :model-value="modelValue"
    as="div"
    by="id"
    @update:model-value="
      (value) => emit('update:modelValue', value)
    "
  >
    <div ref="listboxReferenceRef">
      <HeadlessComboboxInput
        :class="[
          'block w-full rounded-md border border-background-fg/8 text-sm font-semibold focus:outline-none sm:text-sm',
          'focus:border-primary focus:ring-primary',
        ]"
        placeholder="Search for existing customer"
        @input="input = $event.target.value"
      />
    </div>

    <Teleport to="body">
      <div ref="listboxOptionsRef" :style="floatingStyles">
        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <HeadlessComboboxOptions
            class="mb-6 max-h-60 w-full overflow-y-auto rounded-lg bg-background shadow"
          >
            <HeadlessComboboxOption
              v-for="customer in customers"
              :key="customer.id"
              #default="{ selected }"
              :value="customer"
            >
              <div
                :class="[
                  selected
                    ? 'bg-background-fg/5'
                    : 'text-background-fg/50 hover:bg-background-fg/1',
                  'cursor-pointer p-2',
                ]"
              >
                <p class="text-lg font-semibold">
                  {{ customer.name }}
                </p>
                <p
                  v-if="customer.email"
                  class="flex items-center text-sm text-background-fg/87"
                >
                  <IconHeroiconsEnvelope class="mr-2" />

                  {{ customer.email }}
                </p>
                <p
                  v-if="customer.phone"
                  class="flex items-center text-sm text-background-fg/87"
                >
                  <IconHeroiconsPhone class="mr-2" />
                  {{ customer.phone }}
                </p>
              </div>
            </HeadlessComboboxOption>
          </HeadlessComboboxOptions>
        </Transition>
      </div>
    </Teleport>
  </HeadlessCombobox>
</template>

<script setup lang="ts">
import type { ListCustomersResponseItem } from "@/services/customers/listCustomers"
import {
  useFloating,
  offset,
  flip,
  autoUpdate,
  size,
} from "@floating-ui/vue"

const props = defineProps<{
  disabled?: boolean
  modelValue?: ListCustomersResponseItem
}>()

const emit = defineEmits<{
  (
    event: "update:modelValue",
    value: ListCustomersResponseItem
  ): void
}>()

const input = ref("")
const query = debouncedRef(input, 300)

const { data } = useCustomers(
  computed(() => ({
    search: query.value,
    limit: 10,
    offset: 0,
  }))
)

const customers = computed(() => data.value?.items)

const listboxOptionsRef = useTemplateRef(
  "listboxOptionsRef"
)
const listboxReferenceRef = useTemplateRef(
  "listboxReferenceRef"
)
const { floatingStyles } = useFloating(
  listboxReferenceRef,
  listboxOptionsRef,

  {
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    ],
  }
)
</script>
